import React from 'react';
import { Box, Typography } from '@mui/material';
import { neutral, statusTone } from '../../theme';
import { pluralize } from '../../utils/format';
import { shareOf, StatusRow } from './insightsModel';
import { visuallyHidden } from './visuallyHidden';

interface StatusBreakdownProps {
  rows: StatusRow[];
}

/**
 * Projects per status as solid bars, each a share of all projects, coloured
 * by the same tone the status chip uses. The count and share are printed on
 * every row, not hidden in a tooltip.
 */
const StatusBreakdown: React.FC<StatusBreakdownProps> = ({ rows }) => {
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  return (
    <Box component="ul" aria-label="Projects by status" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', rowGap: 2 }}>
      {rows.map((row) => {
        const share = shareOf(row.count, total);
        const color = statusTone[row.tone].main;
        return (
          <Box component="li" key={row.label} data-testid="status-row" sx={{ minWidth: 0 }}>
            <Box component="span" sx={visuallyHidden}>
              {`${row.label}: ${pluralize(row.count, 'project')}, ${share}% of all projects`}
            </Box>
            <Box
              aria-hidden="true"
              sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 0.75 }}
            >
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }} noWrap>
                  {row.label}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: 'text.secondary' }}
              >
                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {row.count}
                </Box>
                {` · ${share}%`}
              </Typography>
            </Box>
            <Box aria-hidden="true" sx={{ height: 8, borderRadius: 1, backgroundColor: neutral[200], overflow: 'hidden' }}>
              <Box sx={{ width: `${share}%`, minWidth: row.count > 0 ? 4 : 0, height: '100%', backgroundColor: color }} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default StatusBreakdown;
